import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SessionInfo } from '@shared/api';
import { ROUTES } from '@shared/api';
import type { Capability } from '@shared/permissions';
import { can as roleCan } from '@shared/permissions';
import { api, setCsrfToken, setUnauthorizedHandler } from './api-client';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export type SessionUser = SessionInfo['user'];
export type SchoolInfo = SessionInfo['school'];

export interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  capabilities: readonly Capability[];
  school: SchoolInfo | null;
  csrfToken: string | null;
  /** Errors with an ApiError (status 401 => bad credentials) for the caller to display. */
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-fetch the session from the server (after role or setting changes). */
  refresh: () => Promise<void>;
  can: (capability: Capability) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const sessionRef = useRef<SessionInfo | null>(null);

  const applySession = useCallback((next: SessionInfo | null): void => {
    sessionRef.current = next;
    setSession(next);
    setCsrfToken(next?.csrf_token ?? null);
    setStatus(next ? 'authenticated' : 'anonymous');
  }, []);

  const loadSession = useCallback(async (): Promise<void> => {
    try {
      const info = await api.get<SessionInfo>(ROUTES.session, undefined, { silent401: true });
      applySession(info && info.user ? info : null);
    } catch {
      // Network problems or an unusable session both leave the user anonymous;
      // the app never shows partial authentication state.
      applySession(null);
    }
  }, [applySession]);

  // Load the session on mount.
  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  // A 401 from any other API call means the session died mid-use: drop it and
  // send the user to the login screen, preserving where they were.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (sessionRef.current) {
        applySession(null);
        navigate('/login', { replace: true });
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [applySession, navigate]);

  const signIn = useCallback(
    async (identifier: string, password: string): Promise<void> => {
      await api.post(ROUTES.login, { identifier, password }, undefined, { silent401: true });
      // The login reply carries only the user. The session endpoint is the one
      // source of capabilities and the CSRF token, so read it: a tab that skips
      // this holds no token and its first save comes back "Missing or invalid
      // CSRF token" until someone refreshes by hand.
      await loadSession();
    },
    [loadSession],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.post(ROUTES.logout, undefined, undefined, { silent401: true });
    } finally {
      applySession(null);
      navigate('/login', { replace: true });
    }
  }, [applySession, navigate]);

  const value = useMemo<AuthContextValue>(() => {
    const capabilities: readonly Capability[] = session?.capabilities ?? [];
    const user = session?.user ?? null;
    return {
      status,
      user,
      capabilities,
      school: session?.school ?? null,
      csrfToken: session?.csrf_token ?? null,
      signIn,
      signOut,
      refresh: loadSession,
      can: (capability: Capability): boolean => {
        if (capabilities.length > 0) return capabilities.includes(capability);
        // Older payloads without a capability list fall back to the central
        // role policy so navigation still works.
        return roleCan(user?.role, capability);
      },
    };
  }, [status, session, signIn, signOut, loadSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return value;
}
